import { Scenario, getSourceId, getPreset } from '@account/account-portal/config'
import { useExternalAuthData } from '@account/account-portal/external-auth'
import {
  Touchpoint,
  getAccountType,
  loginUser,
  registerUser,
  startPasswordReset,
  useLightRegistrationStatus,
  useLoginStatus,
  useRegistrationStatus,
  usePasswordResetStartStatus,
} from '@account/account-portal/lib'
import { AccountType, LoginError, RegistrationData } from '@account/account-portal/types'
import { Consents } from '@account/consents'
import { showErrors as showConsentErrors } from '@account/consents-shared-lib'
import { GlButton } from '@adl/component-button'
import { GlForm } from '@adl/component-form'
import { GlInput } from '@adl/component-input'
import { useConfig } from '@crystal/config'
import { useDevice, useMfProps } from '@crystal/core'
import { useTranslations } from '@crystal/translator'
import { FC, useEffect, useRef, useState } from 'react'

import { AccountPortalConfig } from '../../config'
import { ScreenViews, useConsents, useHydration, useViewData } from '../../lib'
import {
  trackAccountType,
  trackActivationStart,
  trackForgetPwStart,
  trackForgetPwSuccess,
  trackFullAccountCreationStart,
  trackFullAccountCreationSuccess,
  trackLoginError,
  trackLoginStart,
  trackLoginSuccess,
} from '../../lib/analytics'
import { triggerActivationEmail } from '../../lib/api-client'
import styles from '../account-portal.module.scss'
import { AgeConfirmationCheckBox } from '../AgeConfirmationCheckBox'
import { ErrorView } from '../ErrorView'
import { PasswordInput } from '../PasswordInput'
import { PersistentLoginCheckbox } from '../PersistentLoginCheckbox'

import { useFormData, useFullAccountValidation } from './account-form'
import { KmcDataStorageConsent } from './KmcDataStorageConsent'

export const OneStepLandingForm: FC = () => {
  const t = useTranslations()
  const { emailPolicy, legalPolicy } = useConfig<AccountPortalConfig>()
  const validator = useFormData<RegistrationData>()
  const { registerField, handleSubmit, setError, getValues, setValue } = validator
  const { onEmailBlur, passwordError, passwordPolicy, accountType: _accountType } = useFullAccountValidation(validator)
  const { isPending: isLightAccountPending } = useLightRegistrationStatus()
  const { isPending: isFullAccountPending } = useRegistrationStatus()
  const { isPending: isLoginPending } = useLoginStatus()
  const { isPending: isResetPasswordPending } = usePasswordResetStartStatus()
  const { isKmcFlowRequired } = useConfig<AccountPortalConfig>()
  const { kmc } = useExternalAuthData()
  const { isMobile } = useDevice()
  const scenarioType = 'registration'
  const consentsState = useConsents(scenarioType)
  const mfProps = useMfProps()
  const { viewData, showScreen, updateViewData } = useViewData()
  const { isHydrated } = useHydration()
  const { isAuthProvider } = getPreset(viewData.preset)
  const { showPersistentLoginCheckbox } = viewData
  const touchpoint = viewData.touchpoint || Touchpoint.Unknown
  const isPending = isLightAccountPending || isFullAccountPending || isLoginPending || isResetPasswordPending
  const registrationSourceId = getSourceId(touchpoint, Scenario.Registration)
  const activationSourceId = getSourceId(touchpoint, Scenario.Activation)
  const prefillData = viewData.pageContext?.prefillData

  const [apiError, _setApiError] = useState<boolean>()
  const setApiError = () => {
    _setApiError(true)
    return undefined
  }
  const clearApiError = () => {
    _setApiError(false)
  }

  const kmcExtension = ({ enabled = false, dataStorageConsent = false }) => {
    if (!enabled) {
      return undefined
    }

    return {
      kmc: {
        userDataToken: kmc?.accessToken || '',
        dormantPeriod: dataStorageConsent ? 'untilWithdraw' : '1y',
      } as RegistrationData['kmc'],
    }
  }

  async function onSubmit({
    email,
    password,
    persistentLogin = !!isAuthProvider,
    ageConfirmation,
    dataStorageConsent,
  }: RegistrationData) {
    clearApiError()
    updateViewData({
      email,
      persistentLogin,
      minAgeConfirmation: ageConfirmation,
    })

    let accountType = _accountType
    if (!accountType) {
      accountType = await getAccountType(email)
        .then(({ accountType }) => accountType)
        .catch(setApiError)
    }

    trackAccountType(accountType)
    if (accountType === AccountType.Full) {
      trackLoginStart()
      return loginUser({
        user: email,
        password,
        persistentLogin,
      })
        .then(() => trackLoginSuccess())
        .catch(err => {
          const { validationErrors, details, isKMCRequired } = err as LoginError
          trackLoginError()

          if (details?.statusCode === 400 && isKMCRequired) {
            return setError('password', { message: t('login.error.kmcRequired') })
          }
          if (details?.statusCode === 400 && validationErrors?.error === 'invalid_grant') {
            setError('password', { message: t('login.error') })
          } else if (details?.statusCode === 401 && validationErrors) {
            setError('password', { message: t('password.error.min.age', t('global.country')) })
          } else {
            setApiError()
          }
        })
    }

    if (accountType === AccountType.NotRegistered) {
      // Only throw Consents error for NotRegistered users
      if (!consentsState?.isValid) {
        showConsentErrors(scenarioType)
        return
      }

      trackFullAccountCreationStart()
      return registerUser({
        email,
        password,
        ageConfirmation: legalPolicy.minAgeRequired ? ageConfirmation : true,
        legalAcceptance: consentsState?.payload.legalAcceptance,
        persistentLogin,
        sourceId: registrationSourceId,
        ...kmcExtension({ enabled: isKmcFlowRequired, dataStorageConsent }),
      })
        .then(() => trackFullAccountCreationSuccess())
        .catch(setApiError)
    }

    if (accountType === AccountType.Light) {
      return await triggerActivationEmail({
        email,
        minAgeConfirmation: true,
        sendActivationEmail: true,
        sourceId: activationSourceId,
      })
        .then(({ acid }) => {
          trackActivationStart(acid)
          showScreen(ScreenViews.ACTIVATE_CONFIRMATION_SCREEN)
        })
        .catch(setApiError)
    }
  }

  const onPasswordReset = async () => {
    // Triggers form validation
    // eslint-disable-next-line @typescript-eslint/no-empty-function
    handleSubmit(() => {})()

    const email = getValues().email
    if (!email) {
      return
    }

    trackForgetPwStart()
    await startPasswordReset({ email })
      .then(() => {
        trackForgetPwSuccess()
        showScreen(ScreenViews.RESET_PASSWORD_CONFIRMATION_SCREEN)
      })
      .catch(setApiError)
  }

  // Retrieve and hold SSR value if user writes on input
  const emailRef = useRef<HTMLInputElement | null>(null)
  useEffect(() => {
    const domValue = emailRef.current?.value
    if (domValue) {
      setValue('email', domValue)
    }
  }, [])

  return (
    <GlForm noValidate onSubmit={handleSubmit(onSubmit)}>
      <GlInput
        ref={emailRef}
        id="email"
        type="email"
        autoComplete="email"
        className="gl-form-item gl-vspace-bpall-small"
        labelText={t('email.label')}
        aria-label={t('email.label')}
        readOnly={prefillData?.email && !prefillData?.email?.editable}
        {...registerField('email', {
          ...emailPolicy,
          required: true,
          onBlur: onEmailBlur,
          errorMessage: t('email.error'),
          value: getValues('email'),
        })}
      />
      <PasswordInput
        id="password"
        className="gl-form-item"
        autoComplete="password"
        labelText={t('password.label')}
        aria-label={t('password.label')}
        {...registerField('password', {
          ...passwordPolicy,
          errorMessage: passwordError,
        })}
      />
      <GlButton
        id="forgot-password-button"
        variant="tertiary"
        className={styles['tertiary-link']}
        onClick={onPasswordReset}>
        {t('password.forgot')}
      </GlButton>
      {legalPolicy.minAgeRequired && (
        <AgeConfirmationCheckBox
          scenarioType={scenarioType}
          {...registerField('ageConfirmation', {
            required: _accountType !== AccountType.Full,
            errorMessage: t('forms.date.tooyoung'),
            type: 'checkbox',
            propsMapper: 'GlCheckbox.v5',
          })}
        />
      )}
      <Consents {...mfProps} explicit scenarioType={scenarioType} />
      {isKmcFlowRequired && (
        <KmcDataStorageConsent
          id="kmcDataStorageConsent"
          className="gl-form-item gl-vspace-bpall-medium"
          aria-label={t('kmc.dataStorageConsent.label')}
          {...registerField('dataStorageConsent')}
        />
      )}
      {showPersistentLoginCheckbox && (
        <PersistentLoginCheckbox
          id="persistentLogin"
          className="gl-form-item gl-vspace-bpall-small"
          aria-label={t('persistent.login.label')}
          {...registerField('persistentLogin')}
        />
      )}
      <ErrorView error={apiError} />
      <GlButton
        fullWidth={isMobile}
        type="submit"
        className="gl-vspace-bpall-small"
        aria-label={t('landing.button.label')}
        loading={isPending}
        disabled={isPending || !isHydrated}
        id="one-step-form-button">
        {t('landing.button.label')}
      </GlButton>
      <Consents {...mfProps} implicit scenarioType={scenarioType} />
    </GlForm>
  )
}
